// SermonsTab — the 3rd bottom-nav tab (redesign Phase 4, rebuilt per
// docs/SERMONS-SPEC.md + docs/designs/SermonsSeries.html).
//
// Trending/BibleMap/Churches are NOT rewritten — they're imported from
// ExploreTab.jsx (already self-contained, prop-driven render functions) and
// composed into this screen's layout.
//
// Props:
//   churchIds              — followed church ids (home + extras), for scoping
//   churches                — full church list (id -> name/logo lookups)
//   activeChurchId           — the member's own home church id
//   sermonNoteChurchIds, setSermonNoteChurchIds — follow state (passed through to ChurchesView)
//   user                     — { id, email, full_name }
//   onOpenVerse(book, chapter, verse, scope)
//   onOpenChurch(entity)
//   onOpenPlanDay(plan, follow, dayNumber) — open ReadingPlanDaySheet
//   onOpenSermon(sermon)                   — open FullSermonSheet
//   openBibleMapSignal — bumped (any changing value) by BibleApp.jsx's
//     "See in Bible Map" deep link to auto-open the Bible Map overlay
//   searchOpen — controlled by BibleApp.jsx's header search button (the
//     button itself lives in the header, outside this component)

import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { supabase } from "../supabase";
import { useSermons } from "../hooks/useSermons";
import { groupSermonsBySeries, groupSermonsBySeriesByChurch, displayTitleOf, firstPassageOf } from "../lib/sermonSeries";
import { TrendingView, BibleMapView, ChurchesView, ChurchCircles, useChurchCircleSelection, CircleLogoOrInitials, CIRCLE_PASTELS } from "./ExploreTab";
import SundaySermonCard from "./SundaySermonCard";
import { prefersReducedMotion } from "./StackedSheet";

const GOLD = "var(--gold)";
const SERIF = "'Source Serif 4', Georgia, serif";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const SERIES_PASTELS = ["var(--peach)", "var(--sage)", "var(--lavender)", "var(--sky)", "var(--blush)"];

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + (d.length <= 10 ? "T00:00:00" : "")).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return d; }
}

function seriesDateRange(sermons) {
  const dates = (sermons || []).map(s => s.date).filter(Boolean).slice().sort();
  if (!dates.length) return "";
  const first = new Date(dates[0]);
  const last = new Date(dates[dates.length - 1]);
  const fmt = d => d.toLocaleDateString("en-GB", { month: "short" });
  return first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()
    ? fmt(first)
    : `${fmt(first)}–${fmt(last)}`;
}

function SeriesStack({ children }) {
  return <div style={S.seriesStack}>{children}</div>;
}

function AllSermonsCard({ count, onClick }) {
  return (
    <button type="button" onClick={onClick} style={S.seriesCardBtn}>
      <SeriesStack>
        <div style={{ ...S.seriesBack, background: GOLD, opacity: 0.45 }}/>
        <div style={{ ...S.seriesMid, background: GOLD, opacity: 0.75 }}/>
        <div style={{ ...S.seriesFront, background: "#F5E4B8", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 8h16M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2M4 8v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/>
          </svg>
        </div>
      </SeriesStack>
      <div style={S.seriesName}>All sermons</div>
      <div style={S.seriesMeta}>{count} sermon{count === 1 ? "" : "s"}</div>
    </button>
  );
}

// `church`: when passed (the multi-church Series carousel,
// docs/FIX-SERMONS-POLISH.md §3), renders a small church chip under the name
// and hides the sermon-count/date-range line ("No count" per the doc).
// Omitted → unchanged single-church behaviour (SERMONS-SPEC.md §4).
function SeriesCard({ name, sermons, thumb, pastel, church, churchPastel, width = 150, onClick }) {
  const count = sermons.length;
  const range = seriesDateRange(sermons);
  return (
    <button type="button" onClick={onClick} style={{ ...S.seriesCardBtn, width }}>
      <SeriesStack>
        <div style={{ ...S.seriesBack, background: pastel, opacity: 0.55 }}/>
        <div style={{ ...S.seriesMid, background: pastel }}/>
        <div style={S.seriesFront}>
          {thumb ? <img src={thumb} alt="" style={S.seriesThumbImg}/> : <div style={{ ...S.seriesThumbImg, background: "var(--border)" }}/>}
        </div>
      </SeriesStack>
      <div style={S.seriesName}>{name}</div>
      {church ? (
        <span style={{ ...S.seriesChipRow, color: churchPastel?.ink || "var(--stone)" }}>
          <span style={{ ...S.seriesChipCircle, background: churchPastel?.bg || "var(--border)", color: churchPastel?.ink || "var(--ink)" }}>
            <CircleLogoOrInitials url={church.logo_url} name={church.name}/>
          </span>
          {church.name}
        </span>
      ) : (
        <div style={S.seriesMeta}>{count} sermon{count === 1 ? "" : "s"}{range ? ` · ${range}` : ""}</div>
      )}
    </button>
  );
}

// sermons.duration is a free-text column — sometimes already formatted
// ("38:12"), sometimes a raw-seconds string from an upload path that never
// formatted it (e.g. "1774"). Tolerate both rather than rendering digits.
function fmtDurationBadge(duration) {
  if (!duration) return "";
  const str = String(duration).trim();
  if (str.includes(":")) return str;
  const n = Number(str);
  if (!Number.isFinite(n) || n <= 0) return "";
  const mins = Math.floor(n / 60);
  const secs = Math.floor(n % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

// Vertical multi-church "Latest sermons" card (docs/designs/SermonsChurches.png)
// — distinct from SundaySermonCard's horizontal layout, which stays in use
// for This Week and the single-church Sermons view. Full-width thumbnail,
// then a church chip (in the church's own circle colour, not gold/uppercase)
// in place of the usual eyebrow, then title/meta/pills. No series line —
// the row already groups by church, not by series.
function LatestSermonCard({ sermon, church, pastel, plan, follow, onOpenSermon, onOpenPlanDay }) {
  const [summary, setSummary] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!sermon?.id) { setSummary(""); return; }
    supabase.from("sermons").select("ai_summary").eq("id", sermon.id).maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error && /ai_summary/i.test(error.message || "")) { setSummary(""); return; }
        setSummary(data?.ai_summary || "");
      });
    return () => { cancelled = true; };
  }, [sermon?.id]);

  const thumbBase = sermon.youtube_id ? `https://i.ytimg.com/vi/${sermon.youtube_id}` : null;
  const refs = sermon.sermon_refs || [];
  const refCount = refs.length;
  const hasKeyMoments = refs.some(r => r.timestamp_s != null || r.timestamp);
  const meta = [firstPassageOf(sermon), fmtDate(sermon.date)].filter(Boolean).join(" · ");

  const pillCandidates = [];
  if (plan) pillCandidates.push({ key: "devo", label: "5-day devotional", style: S.pillDevo, onClick: e => { e.stopPropagation(); onOpenPlanDay?.(plan, follow, follow?.last_opened_day || 1); } });
  if (summary) pillCandidates.push({ key: "summary", label: "Summary", style: S.pillSummary });
  if (refCount > 0) pillCandidates.push({ key: "refs", label: `${refCount} Bible ref${refCount === 1 ? "" : "s"}`, style: S.pillRefs });
  if (hasKeyMoments) pillCandidates.push({ key: "moments", label: "Key moments", style: S.pillMoments });
  const shownPills = pillCandidates.slice(0, 2);
  const overflowCount = pillCandidates.length - shownPills.length;

  return (
    <div style={S.latestCard} onClick={() => onOpenSermon?.(sermon)}>
      <div style={S.latestThumbWrap}>
        {thumbBase ? (
          <img src={`${thumbBase}/maxresdefault.jpg`} alt="" style={S.thumb}
            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = `${thumbBase}/hqdefault.jpg`; }}/>
        ) : <div style={{ ...S.thumb, background: "var(--border)" }}/>}
        <div style={S.latestPlayDot}>▶</div>
        {fmtDurationBadge(sermon.duration) && <div style={S.durationBadge}>{fmtDurationBadge(sermon.duration)}</div>}
      </div>
      <div style={S.latestChipRow}>
        <span style={{ ...S.latestChipCircle, background: pastel.bg, color: pastel.ink }}>
          <CircleLogoOrInitials url={church?.logo_url} name={church?.name}/>
        </span>
        <span style={{ ...S.latestChipName, color: pastel.ink }}>{church?.name || "Church"}</span>
      </div>
      <div style={S.latestTitle}>{displayTitleOf(sermon)}</div>
      <div style={S.latestMeta}>{meta}</div>
      {shownPills.length > 0 && (
        <div style={S.pillsRow}>
          {shownPills.map(p => <span key={p.key} style={p.style} onClick={p.onClick}>{p.label}</span>)}
          {overflowCount > 0 && <span style={S.pillMore}>+{overflowCount}</span>}
        </div>
      )}
    </div>
  );
}

export default function SermonsTab({
  churchIds = [], churches = [], activeChurchId,
  sermonNoteChurchIds = [], setSermonNoteChurchIds = () => {},
  user, isModerator, onOpenVerse, onOpenChurch, onOpenPlanDay, onOpenSermon,
  sermonBehindKind, openBibleMapSignal, searchOpen, onToggleSearch,
}) {
  const [search, setSearch] = useState("");
  const [bibleMapOpen, setBibleMapOpen] = useState(false);
  const [churchesOpen, setChurchesOpen] = useState(false);
  const [seriesListOpen, setSeriesListOpen] = useState(false);
  const [sermonListTarget, setSermonListTarget] = useState(null); // { title, sermons } | null
  const [trendingOverlayOpen, setTrendingOverlayOpen] = useState(false);

  // Auto-open the Bible Map overlay when BibleApp.jsx's "See in Bible Map"
  // deep link bumps this signal (mirrors the allPlansOpen pattern from 3a).
  useEffect(() => { if (openBibleMapSignal != null) setBibleMapOpen(true); }, [openBibleMapSignal]);

  const churchById = useMemo(() => {
    const m = new Map();
    for (const c of churches || []) m.set(c.id, c);
    return m;
  }, [churches]);

  const followedIds = useMemo(() => {
    const s = new Set((churchIds || []).filter(Boolean));
    if (activeChurchId) s.add(activeChurchId);
    return s;
  }, [churchIds, activeChurchId]);

  // Church circles pool — home first, then followed churches (docs/SERMONS-SPEC.md §2).
  const circlesPool = useMemo(() => {
    const list = [];
    if (activeChurchId && churchById.get(activeChurchId)) list.push(churchById.get(activeChurchId));
    for (const id of followedIds) {
      if (id === activeChurchId) continue;
      const c = churchById.get(id);
      if (c) list.push(c);
    }
    return list;
  }, [followedIds, activeChurchId, churchById]);
  const poolIds = useMemo(() => circlesPool.map(c => c.id), [circlesPool]);
  const { selectedIds, toggle: toggleCircle, selectAll: selectAllCircles, clear: clearCircles } = useChurchCircleSelection(user?.id, poolIds);

  // Same colour each church gets in the circles row above (ChurchCircles in
  // ExploreTab.jsx) — home is always lavender, everyone else cycles through
  // CIRCLE_PASTELS by their position in the shared pool, so a church's
  // colour matches between the circle and its "Latest sermons" card.
  function pastelForChurch(churchId) {
    if (churchId === activeChurchId) return { bg: "var(--lavender)", ink: "var(--lavender-ink)" };
    const i = poolIds.indexOf(churchId);
    return CIRCLE_PASTELS[(i < 0 ? 0 : i) % CIRCLE_PASTELS.length];
  }

  const isSingleSelected = selectedIds.length === 1;
  const singleChurchId = isSingleSelected ? selectedIds[0] : null;
  const singleChurchIsHome = singleChurchId === activeChurchId;

  // "Latest sermon" reflects whichever single church is selected (home or a
  // single followed church) — docs/SERMONS-SPEC.md §3. With several churches
  // selected, the multi-church "Latest sermons" row (below) takes over instead.
  const { sermons: singleChurchSermons } = useSermons(isSingleSelected ? [singleChurchId] : []);
  const thisSunday = isSingleSelected ? (singleChurchSermons[0] || null) : null;

  const scopeChurchIds = selectedIds.length ? selectedIds : [];
  const { sermons: scopedSermons, loading: sermonsLoading } = useSermons(scopeChurchIds);
  const searchedSermons = useMemo(() => {
    if (!search.trim()) return scopedSermons;
    const q = search.trim().toLowerCase();
    return scopedSermons.filter(s => (s.title || "").toLowerCase().includes(q));
  }, [scopedSermons, search]);

  const seriesGroups = useMemo(() => groupSermonsBySeries(searchedSermons), [searchedSermons]);

  // Multi-church Series carousel: grouped per-church (never merges two
  // churches' same-named series), home church's series pinned first, then
  // newest activity (same ordering convention as latestPerChurch below).
  const seriesGroupsByChurch = useMemo(() => {
    const groups = groupSermonsBySeriesByChurch(searchedSermons);
    return groups.slice().sort((a, b) => {
      if (a.churchId === activeChurchId && b.churchId !== activeChurchId) return -1;
      if (b.churchId === activeChurchId && a.churchId !== activeChurchId) return 1;
      return (b.sermons[0]?.date || "").localeCompare(a.sermons[0]?.date || "");
    });
  }, [searchedSermons, activeChurchId]);

  // Series overlay's own month-range filter — on top of the same church
  // selection (selectedIds/searchedSermons), local to the overlay so it
  // doesn't affect the carousel preview on the main tab. "YYYY-MM" strings
  // from <input type="month">, compared directly against sermons.date's
  // own "YYYY-MM-DD..." prefix — no schema change needed.
  const [seriesFromMonth, setSeriesFromMonth] = useState("");
  const [seriesToMonth, setSeriesToMonth] = useState("");
  const seriesDateFilteredSermons = useMemo(() => {
    if (!seriesFromMonth && !seriesToMonth) return searchedSermons;
    return searchedSermons.filter(s => {
      if (!s.date) return false;
      const m = s.date.slice(0, 7);
      if (seriesFromMonth && m < seriesFromMonth) return false;
      if (seriesToMonth && m > seriesToMonth) return false;
      return true;
    });
  }, [searchedSermons, seriesFromMonth, seriesToMonth]);
  const seriesGroupsFiltered = useMemo(() => groupSermonsBySeries(seriesDateFilteredSermons), [seriesDateFilteredSermons]);
  const seriesGroupsByChurchFiltered = useMemo(() => {
    const groups = groupSermonsBySeriesByChurch(seriesDateFilteredSermons);
    return groups.slice().sort((a, b) => {
      if (a.churchId === activeChurchId && b.churchId !== activeChurchId) return -1;
      if (b.churchId === activeChurchId && a.churchId !== activeChurchId) return 1;
      return (b.sermons[0]?.date || "").localeCompare(a.sermons[0]?.date || "");
    });
  }, [seriesDateFilteredSermons, activeChurchId]);

  // Multi-church mode: one card per selected church, home first then newest
  // activity, plus the "All sermons" / "Series" tiles' 3-thumbnail stacks.
  const latestPerChurch = useMemo(() => {
    if (isSingleSelected) return [];
    const byChurch = new Map();
    for (const s of scopedSermons) {
      const cur = byChurch.get(s.church_id);
      if (!cur || (s.date || "") > (cur.date || "")) byChurch.set(s.church_id, s);
    }
    const rows = [...byChurch.entries()].map(([churchId, sermon]) => ({ churchId, sermon }));
    rows.sort((a, b) => {
      if (a.churchId === activeChurchId) return -1;
      if (b.churchId === activeChurchId) return 1;
      return (b.sermon.date || "") < (a.sermon.date || "") ? -1 : 1;
    });
    return rows;
  }, [scopedSermons, isSingleSelected, activeChurchId]);

  // Which sermons have a published plan — a simple presence check, not a
  // full fetch (tapping a card opens FullSermonSheet, which does its own plan
  // lookup). Scoped to the member's home church only: RLS only ever lets a
  // member read their OWN church's published reading_plans, so querying other
  // followed churches here would just come back empty — not a bug to work
  // around, matches the same home-church-only scoping PlansTab always used.
  const [plannedSermonIds, setPlannedSermonIds] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    if (!activeChurchId) { setPlannedSermonIds(new Set()); return; }
    supabase.from("reading_plans").select("sermon_id")
      .eq("church_id", activeChurchId).eq("status", "published")
      .then(({ data }) => { if (!cancelled) setPlannedSermonIds(new Set((data || []).map(r => r.sermon_id))); });
    return () => { cancelled = true; };
  }, [activeChurchId]);

  // The latest sermon's own plan (for the "5-day devotional" button) + the user's
  // follow row for it, fetched by sermon_id — same reverse-lookup pattern
  // FullSermonSheet already uses.
  const [sundayPlan, setSundayPlan] = useState(null);
  const [sundayFollow, setSundayFollow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!thisSunday?.id) { setSundayPlan(null); setSundayFollow(null); return; }
      const { data: plan } = await supabase.from("reading_plans")
        .select("*, sermons(id, title, preacher, date, youtube_id, youtube_url, duration, church_id), reading_plan_days(*)")
        .eq("sermon_id", thisSunday.id).eq("status", "published").maybeSingle();
      if (cancelled) return;
      if (plan) plan.reading_plan_days = (plan.reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number);
      setSundayPlan(plan || null);
      if (plan && user?.id) {
        const { data: follow } = await supabase.from("reading_plan_follows")
          .select("*").eq("plan_id", plan.id).eq("user_id", user.id).maybeSingle();
        if (!cancelled) setSundayFollow(follow || null);
      } else {
        setSundayFollow(null);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [thisSunday?.id, user?.id]);

  // Series overlay target for the Latest-sermon card's series line (its own
  // homeSermons lookup — SundaySermonCard just reports the key it computed).
  function sermonSeriesKey(s) {
    if (Array.isArray(s?.playlists) && s.playlists.length) return (s.playlists.find(p => p && p.trim()) || "").trim();
    return (s?.series || "").trim();
  }
  function openThisSundaySeries(key) {
    const matches = singleChurchSermons.filter(s => sermonSeriesKey(s) === key);
    setSermonListTarget({ title: key, sermons: matches });
  }

  return (
    <div style={S.wrap}>
      <style>{".sermons-hscroll{scrollbar-width:none}.sermons-hscroll::-webkit-scrollbar{display:none}"}</style>
      <div style={S.titleRow}>
        <div style={S.title}>Sermons</div>
        <button type="button" onClick={onToggleSearch} aria-label="Search sermons" style={S.searchBtn}>
          <Search size={20} strokeWidth={1.8}/>
        </button>
      </div>

      {searchOpen && (
        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search sermons by title…" style={S.searchInput}/>
      )}

      <ChurchCircles pool={circlesPool} homeChurchId={activeChurchId} selectedIds={selectedIds}
        onToggle={toggleCircle} onSelectAll={selectAllCircles} onClear={() => clearCircles(activeChurchId)}
        onFindChurches={() => setChurchesOpen(true)}/>

      {isSingleSelected ? (
        <>
          {thisSunday && (
            <div style={{ marginTop: 14 }}>
              <SundaySermonCard
                sermon={thisSunday} size="compact" eyebrow={singleChurchIsHome ? "Latest sermon" : `Latest · ${churchById.get(singleChurchId)?.name || ""}`}
                plan={sundayPlan} follow={sundayFollow}
                onOpenSermon={onOpenSermon} onOpenPlanDay={onOpenPlanDay}
                onOpenSeries={openThisSundaySeries}
              />
            </div>
          )}

          {searchedSermons.length > 0 && (
            <div style={S.section}>
              <div style={S.sectionHeadRow}>
                <h2 style={S.h2}>Series</h2>
                <button type="button" onClick={() => setSeriesListOpen(true)} style={S.seeAllLink}>See all</button>
              </div>
              <div className="sermons-hscroll" style={S.seriesScroll}>
                <AllSermonsCard count={searchedSermons.length} onClick={() => setSermonListTarget({ title: "All sermons", sermons: searchedSermons })}/>
                {seriesGroups.map((g, i) => (
                  <SeriesCard key={g.name} name={g.name} sermons={g.sermons} thumb={g.thumb}
                    pastel={SERIES_PASTELS[i % SERIES_PASTELS.length]}
                    onClick={() => setSermonListTarget({ title: g.name, sermons: g.sermons })}/>
                ))}
              </div>
            </div>
          )}

          {!sermonsLoading && !thisSunday && searchedSermons.length === 0 && (
            <div style={S.empty}>No sermons yet. Check back after Sunday!</div>
          )}
        </>
      ) : (
        <>
          {latestPerChurch.length > 0 && (
            <div style={S.section}>
              <div style={S.sectionHeadRow}>
                <h2 style={S.h2}>Latest sermons</h2>
                <button type="button" onClick={() => setSermonListTarget({ title: "All sermons", sermons: searchedSermons })} style={S.seeAllLink}>View all sermons</button>
              </div>
              <div className="sermons-hscroll" style={S.multiScroll}>
                {latestPerChurch.map(row => (
                  <div key={row.churchId} style={S.multiCard}>
                    <LatestSermonCard
                      sermon={row.sermon} church={churchById.get(row.churchId)} pastel={pastelForChurch(row.churchId)}
                      plan={null} follow={null}
                      onOpenSermon={onOpenSermon} onOpenPlanDay={onOpenPlanDay}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {seriesGroupsByChurch.length > 0 && (
            <div style={S.section}>
              <div style={S.sectionHeadRow}>
                <h2 style={S.h2}>Series</h2>
                <button type="button" onClick={() => setSeriesListOpen(true)} style={S.seeAllLink}>View all</button>
              </div>
              <div className="sermons-hscroll" style={S.seriesScroll}>
                {seriesGroupsByChurch.map((g, i) => (
                  <SeriesCard key={`${g.churchId}::${g.name}`} name={g.name} sermons={g.sermons} thumb={g.thumb}
                    pastel={SERIES_PASTELS[i % SERIES_PASTELS.length]} width={156}
                    church={churchById.get(g.churchId)} churchPastel={pastelForChurch(g.churchId)}
                    onClick={() => { setSermonListTarget({ title: g.name, sermons: g.sermons }); }}/>
                ))}
              </div>
            </div>
          )}

          {!sermonsLoading && searchedSermons.length === 0 && (
            <div style={S.empty}>No sermons yet from these churches.</div>
          )}
        </>
      )}

      <div style={S.section}>
        <TrendingView followedIds={followedIds} homeChurchId={activeChurchId} churchById={churchById}
          onOpenVerse={onOpenVerse} churchIds={selectedIds} display="pills"
          heading="Popular verses" subtitle=""
          countMode="refs"
          defaultPeriod={isSingleSelected ? "alltime" : "month"}/>
        <button type="button" onClick={() => setTrendingOverlayOpen(true)} style={{ ...S.seeAllLink, display: "block", marginTop: 8 }}>See all</button>
      </div>

      <div style={S.cardGrid}>
        <button style={S.navCard} onClick={() => setBibleMapOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z"/><path d="M9 3v15M15 6v15"/></svg>
          <div style={S.navTitle}>Bible map</div>
          <div style={S.navSub}>What's been preached</div>
        </button>
        <button style={S.navCard} onClick={() => setChurchesOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v5M10 4h4M5 21V11l7-4 7 4v10M9 21v-5h6v5"/></svg>
          <div style={S.navTitle}>Find churches</div>
          <div style={S.navSub}>Follow other churches</div>
        </button>
      </div>

      {bibleMapOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "var(--cream)", overflowY: "auto" }}>
          <div style={{ paddingTop: "calc(env(safe-area-inset-top) + 16px)" }}>
            <BibleMapView followedIds={followedIds} homeChurchId={activeChurchId} churchById={churchById}
              user={user} isModerator={isModerator} sermonNoteChurchIds={sermonNoteChurchIds} setSermonNoteChurchIds={setSermonNoteChurchIds}
              onClose={() => setBibleMapOpen(false)}
              onOpenVerse={(...args) => { setBibleMapOpen(false); onOpenVerse?.(...args); }}
              onOpenSermon={onOpenSermon} sermonStackedOverThis={sermonBehindKind === "book"}/>
          </div>
        </div>
      )}
      {churchesOpen && (
        <Overlay title="Find churches" onClose={() => setChurchesOpen(false)}>
          <ChurchesView churchById={churchById} activeChurchId={activeChurchId}
            sermonNoteChurchIds={sermonNoteChurchIds} setSermonNoteChurchIds={setSermonNoteChurchIds}
            user={user} onOpenChurch={(e) => { setChurchesOpen(false); onOpenChurch?.(e); }}/>
        </Overlay>
      )}
      {seriesListOpen && (
        <Overlay title="Series" onClose={() => setSeriesListOpen(false)}>
          <div style={{ padding: "0 16px" }}>
            <ChurchCircles pool={circlesPool} homeChurchId={activeChurchId} selectedIds={selectedIds}
              onToggle={toggleCircle} onSelectAll={selectAllCircles} onClear={clearCircles}/>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <input type="month" value={seriesFromMonth} onChange={e => setSeriesFromMonth(e.target.value)}
                style={S.seriesMonthInput} aria-label="From month"/>
              <span style={{ fontSize: 12.5, color: "var(--stone)" }}>to</span>
              <input type="month" value={seriesToMonth} onChange={e => setSeriesToMonth(e.target.value)}
                style={S.seriesMonthInput} aria-label="To month"/>
              {(seriesFromMonth || seriesToMonth) && (
                <button type="button" onClick={() => { setSeriesFromMonth(""); setSeriesToMonth(""); }}
                  style={{ background: "none", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS }}>
                  Clear
                </button>
              )}
            </div>
          </div>
          <div style={S.seriesListGrid}>
            {isSingleSelected ? (
              <>
                {seriesGroupsFiltered.length === 0 && <div style={S.empty}>No series yet.</div>}
                {seriesGroupsFiltered.map((g, i) => (
                  <SeriesCard key={g.name} name={g.name} sermons={g.sermons} thumb={g.thumb}
                    pastel={SERIES_PASTELS[i % SERIES_PASTELS.length]}
                    onClick={() => { setSeriesListOpen(false); setSermonListTarget({ title: g.name, sermons: g.sermons }); }}/>
                ))}
              </>
            ) : (
              <>
                {seriesGroupsByChurchFiltered.length === 0 && <div style={S.empty}>No series yet.</div>}
                {seriesGroupsByChurchFiltered.map((g, i) => (
                  <SeriesCard key={`${g.churchId}::${g.name}`} name={g.name} sermons={g.sermons} thumb={g.thumb}
                    pastel={SERIES_PASTELS[i % SERIES_PASTELS.length]} width={156}
                    church={churchById.get(g.churchId)} churchPastel={pastelForChurch(g.churchId)}
                    onClick={() => { setSeriesListOpen(false); setSermonListTarget({ title: g.name, sermons: g.sermons }); }}/>
                ))}
              </>
            )}
          </div>
        </Overlay>
      )}
      {sermonListTarget && (
        <Overlay title={sermonListTarget.title} onClose={() => setSermonListTarget(null)} behind={sermonBehindKind === "sermonList"}>
          <div style={S.sermonListWrap}>
            {sermonListTarget.sermons.map(s => (
              <div key={s.id} style={S.sermonListRow} onClick={() => onOpenSermon?.(s, { behindKind: "sermonList", backLabel: sermonListTarget.title })}>
                <div style={S.sermonListThumbWrap}>
                  {s.youtube_id
                    ? <img src={`https://i.ytimg.com/vi/${s.youtube_id}/mqdefault.jpg`} alt="" style={S.thumb}/>
                    : <div style={{ ...S.thumb, background: "var(--border)" }}/>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.sermonListTitle}>{displayTitleOf(s)}</div>
                  <div style={S.sermonListMeta}>{[firstPassageOf(s), fmtDate(s.date), s.preacher].filter(Boolean).join(" · ")}</div>
                  {plannedSermonIds.has(s.id) && <div style={S.planTag}>5-day plan</div>}
                </div>
              </div>
            ))}
          </div>
        </Overlay>
      )}
      {trendingOverlayOpen && (
        <Overlay title="Trending" onClose={() => setTrendingOverlayOpen(false)}>
          <div style={{ padding: "0 16px 16px" }}>
            <TrendingView followedIds={followedIds} homeChurchId={activeChurchId} churchById={churchById}
              onOpenVerse={(...args) => { setTrendingOverlayOpen(false); onOpenVerse?.(...args); }}/>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ title, onClose, children, behind }) {
  return (
    <div style={{
      ...S.overlay,
      transform: behind ? "translateY(-10px) scale(0.93)" : "none",
      filter: behind ? "brightness(0.82)" : "none",
      borderRadius: behind ? 26 : 0,
      transition: prefersReducedMotion() ? "none" : "transform .42s cubic-bezier(.2,.8,.2,1), filter .42s ease, border-radius .42s ease",
    }}>
      <div style={S.overlayHeader}>
        <div style={{ fontFamily: SERIF, fontSize: 17, color: "var(--ink)" }}>{title}</div>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>
    </div>
  );
}

const S = {
  // Top is a plain 12px, not calc(env(safe-area-inset-top) + 12px) — .app
  // (the ancestor) already applies that env() once; re-adding it here would
  // double the gap on notched phones. Sides match the other 3 tabs (22px).
  wrap: { padding: "12px 22px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, maxWidth: 560, margin: "0 auto", overflowX: "hidden" },
  titleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 },
  title: { fontFamily: SANS, fontWeight: 800, fontSize: 30, color: "var(--ink)", letterSpacing: "-0.02em" },
  searchBtn: { flexShrink: 0, width: 44, height: 44, borderRadius: 22, border: "none", background: "var(--white)", color: "var(--ink)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  searchInput: { width: "100%", background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: "12px 14px", color: "var(--ink)", fontSize: 15, fontFamily: SANS, boxSizing: "border-box", outline: "none", marginBottom: 14 },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },

  thumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  section: { marginTop: 24 },
  sectionHeadRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 },
  h2: { margin: 0, fontSize: 18, fontWeight: 700, color: "var(--ink)" },
  seeAllLink: { background: "none", border: "none", color: "var(--gold-ink)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 },

  seriesScroll: { display: "flex", gap: 14, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 2, marginRight: -22 },
  multiScroll: { display: "flex", gap: 12, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 2, marginRight: -22 },
  multiCard: { width: 268, flexShrink: 0 },

  latestCard: { background: "var(--white)", borderRadius: 24, padding: 12, display: "flex", flexDirection: "column", gap: 8, cursor: "pointer", fontFamily: SANS },
  latestThumbWrap: { position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 16, overflow: "hidden", background: "var(--border)" },
  latestPlayDot: { position: "absolute", left: "50%", top: "50%", width: 44, height: 44, marginLeft: -22, marginTop: -22, borderRadius: 22, background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink)", fontSize: 16 },
  durationBadge: { position: "absolute", right: 8, bottom: 8, background: "rgba(31,27,22,0.75)", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "3px 7px" },
  latestChipRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 2 },
  latestChipCircle: { width: 20, height: 20, borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, flexShrink: 0 },
  latestChipName: { fontSize: 12, fontWeight: 700 },
  latestTitle: { fontSize: 17, fontWeight: 800, color: "var(--ink)", lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  latestMeta: { fontSize: 12.5, color: "var(--stone)" },

  pillsRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  pillDevo: { display: "inline-flex", alignItems: "center", background: GOLD, color: "var(--ink)", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", fontFamily: SANS },
  pillSummary: { display: "inline-flex", alignItems: "center", background: "#F5E4B8", color: "#7A5A12", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
  pillRefs: { display: "inline-flex", alignItems: "center", background: "#DCD3F5", color: "#2E2552", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
  pillMoments: { display: "inline-flex", alignItems: "center", background: "#CFE0F2", color: "#253A50", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
  pillMore: { display: "inline-flex", alignItems: "center", background: "var(--border)", color: "var(--stone)", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },

  seriesListGrid: { display: "flex", flexWrap: "wrap", gap: 14, padding: 16 },
  seriesMonthInput: { flex: 1, minWidth: 0, background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: SANS, color: "var(--ink)", outline: "none" },
  seriesCardBtn: { width: 150, flexShrink: 0, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  seriesStack: { position: "relative", width: "100%", height: 97 },
  seriesBack: { position: "absolute", left: 14, right: 14, top: 0, height: 84, borderRadius: 12 },
  seriesMid: { position: "absolute", left: 7, right: 7, top: 6, height: 84, borderRadius: 12 },
  seriesFront: { position: "absolute", left: 0, right: 0, top: 13, height: 84, borderRadius: 12, overflow: "hidden", boxShadow: "0 6px 14px rgba(31,27,22,0.18)" },
  seriesThumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  seriesName: { fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25, marginTop: 8, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  seriesMeta: { fontSize: 12, color: "var(--stone)", marginTop: 2 },
  seriesChipRow: { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, marginTop: -2 },
  seriesChipCircle: { width: 15, height: 15, borderRadius: 8, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800, flexShrink: 0 },

  sermonListWrap: { display: "flex", flexDirection: "column", gap: 10, padding: 16 },
  sermonListRow: { display: "flex", gap: 12, alignItems: "center", cursor: "pointer" },
  sermonListThumbWrap: { flexShrink: 0, width: 96, height: 54, borderRadius: 10, overflow: "hidden", background: "var(--border)" },
  sermonListTitle: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25 },
  sermonListMeta: { fontSize: 12, color: "var(--stone)", marginTop: 2 },
  planTag: { display: "inline-block", marginTop: 4, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--gold-ink)", background: "rgba(232,200,122,0.15)", borderRadius: 6, padding: "2px 6px" },

  cardGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 24 },
  navCard: { textAlign: "left", borderRadius: 22, background: "var(--white)", border: "none", padding: 14, display: "flex", flexDirection: "column", gap: 6, cursor: "pointer", fontFamily: SANS },
  navTitle: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)" },
  navSub: { fontSize: 12, color: "var(--stone)" },

  overlay: { position: "fixed", inset: 0, zIndex: 500, background: "var(--cream)", display: "flex", flexDirection: "column" },
  overlayHeader: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 10px", borderBottom: "1px solid var(--border)" },
  closeBtn: { background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", width: 34, height: 34, borderRadius: 999, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" },
};
